import { useTranslation } from 'react-i18next'
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { ProcessorDetails } from '@/lib/api/processors'

interface CheckoutConfigurationCardProps {
    processor: ProcessorDetails
}

const formatDate = (date: Date) => {
    return new Date(date).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
    })
}

const getClientId = (options: Record<string, unknown> | null) => {
    return options?.clientId as string | undefined
}

const getApiKey = (options: Record<string, unknown> | null) => {
    return options?.apiKey as string | undefined
}

const getSandboxed = (options: Record<string, unknown> | null) => {
    return options?.sandboxed === true
}

export const CheckoutConfigurationCard = ({ processor }: CheckoutConfigurationCardProps) => {
    const { t } = useTranslation('processors')
    const clientId = getClientId(processor.options)
    const apiKey = getApiKey(processor.options)
    const isSandboxed = getSandboxed(processor.options)
    const authType = apiKey ? 'apiKey' : 'clientCredentials'

    return (
        <Card className="mb-6">
            <CardHeader>
                <CardTitle>{t('checkoutConfiguration')}</CardTitle>
            </CardHeader>
            <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                        <p className="text-sm text-muted-foreground">{t('authenticationType')}</p>
                        <Badge variant="outline">
                            {authType === 'apiKey' ? t('apiKeyAuth') : t('clientCredentialsAuth')}
                        </Badge>
                    </div>

                    {clientId && (
                        <div>
                            <p className="text-sm text-muted-foreground">{t('clientId')}</p>
                            <p className="font-mono text-sm">{clientId}</p>
                        </div>
                    )}

                    {apiKey && (
                        <div>
                            <p className="text-sm text-muted-foreground">{t('apiKey')}</p>
                            <p className="font-mono text-sm">••••••••••••••••</p>
                        </div>
                    )}

                    {clientId && !apiKey && (
                        <div>
                            <p className="text-sm text-muted-foreground">{t('clientSecret')}</p>
                            <p className="font-mono text-sm">••••••••••••••••</p>
                        </div>
                    )}

                    <div>
                        <p className="text-sm text-muted-foreground">{t('environment')}</p>
                        <Badge variant={isSandboxed ? 'secondary' : 'default'}>
                            {isSandboxed ? t('sandbox') : t('production')}
                        </Badge>
                    </div>

                    {processor.baseCurrency && (
                        <div>
                            <p className="text-sm text-muted-foreground">{t('baseCurrency')}</p>
                            <p className="font-semibold">{processor.baseCurrency}</p>
                        </div>
                    )}

                    <div>
                        <p className="text-sm text-muted-foreground">{t('createdAt')}</p>
                        <p className="font-semibold">{formatDate(processor.createdAt)}</p>
                    </div>

                    <div>
                        <p className="text-sm text-muted-foreground">{t('updatedAt')}</p>
                        <p className="font-semibold">{formatDate(processor.updatedAt)}</p>
                    </div>

                    {processor.supportedCurrencies && processor.supportedCurrencies.length > 0 && (
                        <div className="col-span-full">
                            <p className="text-sm text-muted-foreground mb-2">{t('supportedCurrencies')}</p>
                            <div className="flex flex-wrap gap-2">
                                {processor.supportedCurrencies.map((currency) => (
                                    <Badge key={currency} variant="outline">
                                        {currency}
                                    </Badge>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            </CardContent>
        </Card>
    )
}

